// Meilisearch-driven faceted filtering for collection pages, opted into
// per-section via main-collection-products.liquid's "Use Meilisearch
// filtering instead" setting. Meilisearch only ever decides WHICH
// products match + facet counts - it never renders a card itself.
// Actual card HTML always comes from a real Shopify request to
// api-product-card.liquid (Section Rendering API), so every card here
// is pixel-identical to a normally-rendered one (quick-add, ratings,
// shipping estimate, badges - all just work, no reimplementation/drift).

(function () {
  const root = document.getElementById('llm-meilisearch-collection');
  const configEl = document.getElementById('llm-meilisearch-config');
  if (!root || !configEl) return;

  const config = JSON.parse(configEl.textContent);
  if (!config.host || !config.searchKey) {
    console.error('[llm-meilisearch] Missing host/searchKey - set them under Theme settings > Meilisearch filtering.');
    root.innerHTML = '<p>Search is not configured yet.</p>';
    return;
  }

  // Points at the drawer's own inner content div, not the outer
  // <facet-filters id="llm-meilisearch-filters"> element itself -
  // that outer element now also holds a real header (title + close
  // button, see main-collection-products.liquid) that this file must
  // not wipe out every time it does filtersEl.innerHTML = ''.
  const filtersEl = document.getElementById('llm-meilisearch-filters-content');
  const gridEl = document.getElementById('llm-meilisearch-grid');
  const countEl = document.getElementById('llm-meilisearch-count');
  // The visible dropdown is the native custom-select.liquid component;
  // its hidden mirror <select> (id="<id>-native") is what actually
  // fires a real 'change' event on each pick - see custom-select.js.
  const sortEl = document.getElementById('llm-meilisearch-sort-native');
  const paginationEl = document.getElementById('llm-meilisearch-pagination');

  const PER_PAGE = config.perPage || 16;
  const MAX_VISIBLE_OPTIONS = config.maxFilterOptions || 7;

  // Fields present in the index that aren't meaningful as checkbox
  // facets (either used internally for scoping, or continuous ranges) -
  // always excluded, regardless of merchant config. `available` is kept
  // here too per owner request (the Availability filter kept reappearing
  // whenever the sync re-seeded its Facet Field Config row with Show as
  // Filter on) - it's still used for sorting sold-out-last, just never
  // shown as its own filter group.
  const STRUCTURAL_EXCLUDED_FIELDS = new Set([
    'collections',
    'price_min',
    'price_max',
    'available',
  ]);

  // Merchant-editable via the "Facet Field Config" metaobject (Content >
  // Metaobjects in Shopify admin, auto-seeded by the sync worker with one
  // row per real field it finds in the catalog - vendor/tags/product_type,
  // every variant option, every product metafield definition). Renaming a
  // field or toggling Show as Filter takes effect on the next page load,
  // no code change or redeploy needed. Keyed by the field name as it
  // actually appears in Meilisearch (metafields use `metafield__<key>`,
  // not the config's raw "metafields.ns.key" identifier - same
  // transform the sync worker uses, see metafieldIndexKey in shopify.js).
  function meilisearchAttributeName(fieldIdentifier) {
    if (!fieldIdentifier.startsWith('metafields.')) return fieldIdentifier;
    const indexKey = fieldIdentifier.replace(/^metafields\./, '').replace(/[^a-zA-Z0-9]/g, '_');
    return `metafield__${indexKey}`;
  }

  // Facet Field Config / Facet Value Mapping are fetched from the sync
  // worker directly (not inlined via Liquid's `shop.metaobjects.<type>.
  // values`, which silently truncates at 50 entries - a real problem
  // once this catalog's messy size values pushed Facet Value Mapping
  // past 1,000 rows). See loadFacetConfig below.
  let FIELD_CONFIG = {};
  let VALUE_LABEL_LOOKUP = {};
  let LABEL_RANK_LOOKUP = {};

  const SYNC_WORKER_URL = 'https://llmedico-meilisearch-sync.llmedico.workers.dev';
  let facetConfigPromise = null;
  function ensureFacetConfigLoaded() {
    if (!facetConfigPromise) facetConfigPromise = loadFacetConfig();
    return facetConfigPromise;
  }
  // Kick off immediately (not awaited here) so it loads in parallel with
  // the first Meilisearch search request instead of adding a sequential
  // round-trip - runSearch() awaits the same (already in-flight) promise.
  ensureFacetConfigLoaded();

  async function loadFacetConfig() {
    try {
      const res = await fetch(`${SYNC_WORKER_URL}/config/facets`);
      const data = await res.json();

      const fieldConfig = {};
      for (const e of data.facetFieldConfig || []) {
        if (!e.field) continue;
        fieldConfig[meilisearchAttributeName(e.field)] = {
          displayName: e.displayName,
          showAsFilter: e.showAsFilter === true || e.showAsFilter === 'true',
          showAsSort: e.showAsSort === true || e.showAsSort === 'true',
          fieldOrder: e.fieldOrder,
        };
      }
      FIELD_CONFIG = fieldConfig;

      const valueLookup = {};
      const rankLookup = {};
      for (const m of data.facetValueMappings || []) {
        if (!m.field || !m.raw || !m.label) continue;
        const attr = meilisearchAttributeName(m.field);
        if (!valueLookup[attr]) valueLookup[attr] = {};
        valueLookup[attr][m.raw] = m.label;
        if (m.sortRank != null) {
          if (!rankLookup[attr]) rankLookup[attr] = {};
          rankLookup[attr][m.label] = m.sortRank;
        }
      }
      VALUE_LABEL_LOOKUP = valueLookup;
      LABEL_RANK_LOOKUP = rankLookup;
    } catch (err) {
      console.error('[llm-meilisearch] failed to load facet config:', err);
    }
  }

  // A field only ever appears in Meilisearch's facetDistribution if the
  // sync already registered it as filterable (which itself only happens
  // when Show as Filter is on) - so this exclusion set only needs to
  // catch the always-registered structural fields, plus the rare case of
  // a field whose config row hasn't synced yet (fail closed: hide it).
  function isExcludedField(field) {
    if (STRUCTURAL_EXCLUDED_FIELDS.has(field)) return true;
    return FIELD_CONFIG[field] ? !FIELD_CONFIG[field].showAsFilter : false;
  }

  const AVAILABLE_VALUE_LABELS = { true: 'In stock', false: 'Out of stock' };

  function humanizeOptionSlug(slug) {
    return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function fieldLabel(field) {
    if (FIELD_CONFIG[field]?.displayName) return FIELD_CONFIG[field].displayName;
    if (field === 'available') return 'Availability';
    if (field.startsWith('options.')) return humanizeOptionSlug(field.slice('options.'.length));
    return humanizeOptionSlug(field);
  }

  function valueLabel(field, value) {
    if (field === 'available') return AVAILABLE_VALUE_LABELS[value] ?? String(value);
    return String(value);
  }

  // Collapses raw facet values sharing the same mapped (or default)
  // display label into one entry - e.g. "MD"/"Medium"/"M" all mapped to
  // "Medium" become a single checkbox whose count is the sum of all
  // three, and which filters by all three raw values when checked.
  // Ordered by Sort Rank (Facet Value Mapping's optional field, e.g. the
  // Youth < X-Small < ... < 5X-Large size ladder) where set, then
  // alphabetically for anything unranked, after the ranked ones.
  function groupFacetValues(field, valueEntries) {
    const groups = new Map();
    for (const [rawValue, count] of valueEntries) {
      const label = (VALUE_LABEL_LOOKUP[field] && VALUE_LABEL_LOOKUP[field][rawValue]) || valueLabel(field, rawValue);
      if (!groups.has(label)) groups.set(label, { label, rawValues: [], count: 0 });
      const group = groups.get(label);
      group.rawValues.push(rawValue);
      group.count += count;
    }
    const rankFor = (label) => (LABEL_RANK_LOOKUP[field] && LABEL_RANK_LOOKUP[field][label]) ?? null;
    return [...groups.values()].sort((a, b) => {
      const rankA = rankFor(a.label);
      const rankB = rankFor(b.label);
      if (rankA != null && rankB != null) return rankA - rankB;
      if (rankA != null) return -1;
      if (rankB != null) return 1;
      return a.label.localeCompare(b.label);
    });
  }

  // { [field]: Set(values) }
  const activeFilters = new Map();
  let currentOffset = 0;
  // Set by filter changes (not pagination) so the grid re-render corrects the window
  // scroll if the shrunken result set left the shopper looking past it. Consumed in
  // renderGrid's finally, which runs even if the render's async card fetches reject —
  // relying on runSearch's own promise to resolve proved unreliable for that.
  let pendingScrollCorrection = false;
  // Set while we programmatically sync the sort <select> during a history restore, so its
  // change handler doesn't treat it as a user action (which would push history / re-search).
  let restoringState = false;

  /* ---- URL / history state ------------------------------------------------------------
     Pagination, filters and sort all lived only in memory, so paging 1->2->3 created no
     history entries and Back left the collection entirely instead of stepping back a page.
     Reflect the full state in the URL (filters as f.<field>=<value>, plus page & sort) and
     push a history entry on each user change, so Back/Forward walk through pages, filters
     and sort. Refresh- and share-safe as a bonus. Unmanaged params (utm_*, etc.) are
     preserved. */
  function currentStateUrl() {
    const p = new URLSearchParams(location.search);
    [...p.keys()].forEach((k) => {
      if (k === 'page' || k === 'sort' || k.startsWith('f.')) p.delete(k);
    });
    // One param per field, values comma-joined — a single param can't be deduped by a
    // server/redirect the way repeated keys can (Shopify's preview redirect drops dup
    // keys). Facet values in this catalog (brands, sizes, materials…) contain no commas.
    for (const [field, values] of activeFilters) {
      if (values.size) p.set('f.' + field, [...values].join(','));
    }
    if (currentSort && currentSort !== 'featured') p.set('sort', currentSort);
    const page = Math.floor(currentOffset / PER_PAGE) + 1;
    if (page > 1) p.set('page', String(page));
    const qs = p.toString();
    return location.pathname + (qs ? '?' + qs : '');
  }

  function applyStateFromUrl() {
    const p = new URLSearchParams(location.search);
    activeFilters.clear();
    currentSort = p.get('sort') || 'featured';
    const page = Math.max(1, parseInt(p.get('page'), 10) || 1);
    currentOffset = (page - 1) * PER_PAGE;
    for (const [key, val] of p.entries()) {
      if (!key.startsWith('f.')) continue;
      const field = key.slice(2);
      if (!activeFilters.has(field)) activeFilters.set(field, new Set());
      val.split(',').forEach((v) => { if (v) activeFilters.get(field).add(v); });
    }
  }

  function pushHistory() {
    history.pushState({ ms: true }, '', currentStateUrl());
  }

  // The <custom-select id="llm-meilisearch-sort"> sits ALONGSIDE the native <select> (id
  // ...-native) and drives it — the native isn't a descendant, so closest() won't find it.
  const sortCustomSelect = document.getElementById('llm-meilisearch-sort');

  function applySortToCustomSelect(el) {
    const valSel = window.CSS && CSS.escape ? CSS.escape(currentSort) : currentSort;
    const opt = el && el.querySelector && el.querySelector('[data-value="' + valSel + '"]');
    if (el && typeof el.selectOption === 'function' && opt) {
      // Guard our change handler so this programmatic sync isn't treated as a user pick.
      restoringState = true;
      el.selectOption(opt);
      restoringState = false;
      return true;
    }
    return false;
  }

  function syncSortUi() {
    if (!sortEl) return;
    // selectOption() updates the visible label, aria-selected AND the native value together
    // (dispatching a native change would move none of the label — the custom-select drives
    // the native, not the reverse).
    if (applySortToCustomSelect(sortCustomSelect)) return;
    // Not upgraded yet on first paint: set the native value now so the sort is correct, and
    // retry the label once the element defines.
    sortEl.value = currentSort;
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('custom-select').then(() => applySortToCustomSelect(sortCustomSelect));
    }
  }
  let currentSort = 'featured';
  let latestRequestId = 0;
  // Initial-load facet deferral: renderFacets() is ~130ms of DOM work (≈0.5s on a throttled
  // phone), and on mobile the facet sidebar lives in a CLOSED drawer — so on the first page load
  // we build the grid first and push the facet build to idle time, off the critical load path.
  // deferInitialFacets flips false after the first search; pendingFacetBuild holds the not-yet-run
  // build so opening the Filter drawer can force it in immediately (see the js-toggle-filters hook).
  let deferInitialFacets = true;
  let pendingFacetBuild = null;
  function scheduleIdle(fn, timeout) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: timeout || 600 });
    } else {
      setTimeout(fn, 1);
    }
  }
  // Some collection pages have no manual "Featured" order to sort by -
  // notably /collections/all (Shopify's virtual all-products route) and
  // smart collections, neither of which is a manual collection, so neither
  // has a `collection_positions.<handle>` field registered as sortable in
  // Meilisearch. Asking Meilisearch to sort by a non-sortable attribute
  // 400s the ENTIRE search ("Something went wrong loading results"). Set
  // once runSearch sees that specific error, so buildSortArray drops the
  // position sort and we fall back to available:desc + Meilisearch's default
  // order for this collection.
  let positionSortUnavailable = false;
  // Rating sort primarily uses rating_rounded (the displayed star) so 4.96 and 5.00 group
  // as one "5.0" bucket, tiebroken by review count. That field is added by a worker deploy
  // + re-index; until the index has it, sorting by it 400s ("not sortable"), so we fall
  // back to the precise `rating` and auto-upgrade once the re-index lands.
  let roundedRatingUnavailable = false;

  // Sold-out products always sink to the bottom, regardless of which
  // sort is active (Featured/Price/Rating) - `available:desc` goes
  // first in every case, then whatever the user actually picked.
  // "featured" is the placeholder value for "no extra sort criteria"
  // (custom-select.liquid needs a real, non-blank option value).
  function buildSortArray() {
    const sort = ['available:desc'];
    if (currentSort === 'rating:desc') {
      // Primary key = the DISPLAYED (rounded) star, so all "5.0" products (4.95-5.00)
      // group together; tiebreak by review count. Falls back to precise rating until the
      // re-index adds rating_rounded (see roundedRatingUnavailable).
      sort.push(roundedRatingUnavailable ? 'rating:desc' : 'rating_rounded:desc', 'rating_count:desc');
    } else if (currentSort === 'featured' || !currentSort) {
      // Matches this collection's own configured order in Shopify admin
      // (manual drag order, best-selling, etc.) - products the last
      // full import couldn't place (e.g. added since, or a collection
      // over the 250-product cap) have no value for this field and
      // sort last automatically (Meilisearch always puts null-valued
      // docs at the end, regardless of direction). Skipped for collections
      // whose position field isn't sortable (see positionSortUnavailable).
      if (!positionSortUnavailable) {
        sort.push(`collection_positions.${config.collectionHandle}:asc`);
      }
    } else {
      sort.push(currentSort);
    }
    return sort;
  }

  // Raw option values routinely contain literal `"` (inches, e.g. `2XL
  // (30" to 56" waist)`) - unescaped, that breaks out of the filter
  // string's own quoting and produces an invalid Meilisearch filter
  // expression (confirmed: caused every filter click to fail with
  // "Something went wrong loading results").
  function escapeFilterValue(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  // /collections/all is Shopify's virtual all-products route - there is no
  // real "all" collection in the index (products only carry the handles of
  // collections they actually belong to), so scoping by collections = "all"
  // matches ZERO products. Treat "all" as "the whole catalog" - no collection
  // scope - everywhere the collection filter is applied.
  const isAllCollection = config.collectionHandle === 'all';

  function buildFilterExpression() {
    const clauses = [];
    if (!isAllCollection) {
      clauses.push(`collections = "${escapeFilterValue(config.collectionHandle)}"`);
    }
    for (const [field, values] of activeFilters) {
      if (values.size === 0) continue;
      const ors = [...values].map((v) => `${field} = "${escapeFilterValue(v)}"`).join(' OR ');
      clauses.push(values.size > 1 ? `(${ors})` : ors);
    }
    return clauses.join(' AND ');
  }

  // Same as buildFilterExpression but omits ONE field's own clause — the basis of
  // disjunctive faceting below.
  function buildFilterExpressionExcluding(excludeField) {
    const clauses = [];
    if (!isAllCollection) {
      clauses.push(`collections = "${escapeFilterValue(config.collectionHandle)}"`);
    }
    for (const [field, values] of activeFilters) {
      if (field === excludeField || values.size === 0) continue;
      const ors = [...values].map((v) => `${field} = "${escapeFilterValue(v)}"`).join(' OR ');
      clauses.push(values.size > 1 ? `(${ors})` : ors);
    }
    return clauses.join(' AND ');
  }

  /* DISJUNCTIVE FACETING — lets a shopper pick more than one value in a group.
     Meilisearch computes facetDistribution over the FILTERED result set, INCLUDING that
     field's own filter. So the moment you tick Brand = Abena, the Brand distribution
     comes back containing only Abena and every other brand disappears from the sidebar —
     there is no way to then add Attends. (Measured on /collections/briefs-tab-style:
     19 brands unfiltered, 1 brand once Abena was selected.)
     NOTE an older comment here asserted Meilisearch "always ignores a field's own filter
     when counting that field's options". That is Algolia's behaviour, not Meilisearch's,
     and it was wrong — verified against the live index before writing this.

     Fix: for every field that currently has a selection, ask for that field's counts
     under all the OTHER filters. One /multi-search request covers all of them, and it's
     fired in parallel with the main search rather than after it. */
  async function fetchDisjunctiveFacets() {
    const fields = [];
    for (const [field, values] of activeFilters) {
      if (values.size > 0) fields.push(field);
    }
    if (!fields.length) return null;

    const queries = fields.map((field) => {
      const q = { indexUid: config.index, q: '', limit: 0, facets: [field] };
      const expr = buildFilterExpressionExcluding(field);
      if (expr) q.filter = expr;
      return q;
    });

    try {
      const res = await fetch(`${config.host}/multi-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.searchKey}`,
        },
        body: JSON.stringify({ queries }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      const merged = {};
      (data.results || []).forEach((result, i) => {
        const field = fields[i];
        const dist = result && result.facetDistribution && result.facetDistribution[field];
        if (dist) merged[field] = dist;
      });
      return merged;
    } catch (err) {
      // Non-fatal: fall back to Meilisearch's own (self-filtered) distribution, which is
      // the pre-existing single-select behaviour rather than a broken page.
      console.error('[llm-meilisearch] disjunctive facets failed:', err);
      return null;
    }
  }

  // True de-duplicated product count for a merged facet group (see the
  // over-counting note where this is called from renderFacets). Reuses
  // every OTHER currently active filter (any existing selection on this
  // SAME field is deliberately dropped, which is also what the disjunctive
  // faceting above does) plus one extra clause for this group's raw
  // values, and reads back the real hit count instead of a sum.
  async function fetchAccurateGroupCount(field, rawValues) {
    const clauses = [];
    if (!isAllCollection) {
      clauses.push(`collections = "${escapeFilterValue(config.collectionHandle)}"`);
    }
    for (const [f, values] of activeFilters) {
      if (f === field || values.size === 0) continue;
      const ors = [...values].map((v) => `${f} = "${escapeFilterValue(v)}"`).join(' OR ');
      clauses.push(values.size > 1 ? `(${ors})` : ors);
    }
    const groupOrs = rawValues.map((v) => `${field} = "${escapeFilterValue(v)}"`).join(' OR ');
    clauses.push(rawValues.length > 1 ? `(${groupOrs})` : groupOrs);

    try {
      const res = await fetch(`${config.host}/indexes/${config.index}/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.searchKey}`,
        },
        body: JSON.stringify({ q: '', filter: clauses.join(' AND '), limit: 0 }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.estimatedTotalHits ?? null;
    } catch (err) {
      console.error('[llm-meilisearch] accurate facet count failed:', err);
      return null;
    }
  }

  async function runSearch() {
    // Kick off the facet-config load but DON'T block the search on it: the search
    // body (buildFilterExpression / buildSortArray) doesn't use the config — only
    // the facet RENDER below does. Awaiting it here used to serialize config-fetch
    // + its ~synchronous value-map processing AHEAD of the search even firing;
    // starting the search now lets the two overlap so results/filters paint sooner.
    // configReady is awaited just before renderFacets.
    const configReady = ensureFacetConfigLoaded();
    const requestId = ++latestRequestId;
    gridEl.setAttribute('aria-busy', 'true');

    // Fired now so it overlaps the main search rather than adding a round trip after it.
    const disjunctiveReady = fetchDisjunctiveFacets();

    const filterExpr = buildFilterExpression();
    const body = {
      q: '',
      facets: ['*'],
      limit: PER_PAGE,
      offset: currentOffset,
      sort: buildSortArray(),
    };
    // Omit an empty filter (the /collections/all case with no active
    // facets) rather than sending filter:"" - search the whole index.
    if (filterExpr) body.filter = filterExpr;

    let data;
    try {
      const res = await fetch(`${config.host}/indexes/${config.index}/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.searchKey}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errText = await res.text();
        // Auto-recover from the "Featured" sort referencing a non-sortable
        // collection_positions field (e.g. /collections/all, smart
        // collections): drop that sort once and retry, instead of dead-
        // ending on the error UI. One retry max - the flag stops
        // buildSortArray re-adding it.
        if (res.status === 400 && /is not sortable/i.test(errText)) {
          // rating_rounded not indexed yet → fall back to precise rating, once.
          if (!roundedRatingUnavailable && /rating_rounded/.test(errText)) {
            roundedRatingUnavailable = true;
            gridEl.setAttribute('aria-busy', 'false');
            return runSearch();
          }
          // Featured sort's per-collection position field isn't sortable → drop it, once.
          if (!positionSortUnavailable && /collection_positions/.test(errText)) {
            positionSortUnavailable = true;
            gridEl.setAttribute('aria-busy', 'false');
            return runSearch();
          }
        }
        throw new Error(`Meilisearch search ${res.status}: ${errText}`);
      }
      data = await res.json();
    } catch (err) {
      console.error('[llm-meilisearch] search failed:', err);
      // Owner reported a real dead end here on mobile - no products, no
      // way forward except reloading the whole page. The known
      // unescaped-quote bug is already fixed (see escapeFilterValue),
      // and a likely remaining cause is a burst of parallel
      // fetchAccurateGroupCount requests (one per facet group per
      // click) sharing rate-limit budget with this main search request
      // - trimmed way down below by skipping hidden/overflow groups.
      // Whatever the cause, a retry button costs nothing and turns a
      // dead end into a one-tap recovery instead of requiring a full
      // page reload.
      const retryBtn = document.createElement('button');
      retryBtn.type = 'button';
      retryBtn.className = 'link';
      retryBtn.textContent = 'Try again';
      retryBtn.addEventListener('click', () => runSearch());
      const li = document.createElement('li');
      li.className = 'col-span-full';
      li.textContent = 'Something went wrong loading results. ';
      li.appendChild(retryBtn);
      gridEl.innerHTML = '';
      gridEl.appendChild(li);
      gridEl.removeAttribute('aria-busy');
      // Reveal state even on error, so the loading skeletons are cleared and the
      // retry message shows instead of an endless shimmer.
      gridEl.setAttribute('data-ms-ready', '1');
      return;
    }

    // A slower, earlier request can resolve after a newer one (e.g.
    // rapid filter toggling) - drop anything that isn't the latest.
    if (requestId !== latestRequestId) return;

    // Visible content first. Count, pagination and the grid do NOT need the facet config or the
    // disjunctive counts (both network round trips), so render them immediately rather than
    // blocking the grid behind those — the grid is what the shopper is looking at.
    renderCount(data.estimatedTotalHits ?? data.hits.length);
    renderPagination(data.estimatedTotalHits ?? data.hits.length);
    await renderGrid(data.hits);

    /* Facets DO need the config (display names, value labels, order) + the disjunctive counts,
       and on mobile they sit in a closed drawer. So on the FIRST (initial) load, defer building
       them to idle time — it's ~130ms of DOM work the shopper can't see until they tap Filter.
       On every later filter/sort search, build synchronously: the shopper IS waiting on the
       updated counts, and deferring there would feel laggy.

       Disjunctive counts overlay any field the shopper has already filtered on, so its other
       options stay listed and a second value can be added; unfiltered fields keep the main
       search's distribution, which correctly narrows as filters are applied. */
    const buildFacets = async () => {
      await configReady;
      const facets = data.facetDistribution || {};
      const disjunctive = await disjunctiveReady;
      if (disjunctive) Object.assign(facets, disjunctive);
      if (requestId !== latestRequestId) return; // a newer search has superseded this one
      pendingFacetBuild = null;
      renderFacets(facets);
    };
    if (deferInitialFacets) {
      deferInitialFacets = false;
      pendingFacetBuild = buildFacets; // let the Filter-drawer hook force it in if opened first
      scheduleIdle(() => { if (pendingFacetBuild) pendingFacetBuild(); }, 600);
    } else {
      await buildFacets();
    }
  }

  function renderCount(total) {
    if (total === 0) {
      countEl.textContent = 'No results';
    } else {
      const start = currentOffset + 1;
      const end = Math.min(currentOffset + PER_PAGE, total);
      countEl.textContent = `Showing ${start}-${end} of ${total} results`;
    }
  }

  // Builds the classic "1 2 3 ... 7 8 9" set of page numbers to show,
  // always keeping the first, last, and a window around the current
  // page - same shape as Shopify's own `paginate.parts`.
  function buildPageParts(currentPage, totalPages) {
    const parts = [];
    const windowSize = 1;
    let lastAdded = 0;

    for (let page = 1; page <= totalPages; page += 1) {
      const inWindow = Math.abs(page - currentPage) <= windowSize;
      if (page === 1 || page === totalPages || inWindow) {
        if (lastAdded && page - lastAdded > 1) parts.push({ gap: true });
        parts.push({ page });
        lastAdded = page;
      }
    }
    return parts;
  }

  function goToPage(page) {
    currentOffset = (page - 1) * PER_PAGE;
    pushHistory();
    runSearch();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* After a FILTER change (not pagination), the result set can shrink a lot. If the shopper
     had scrolled down through a long list and then picked a value with only a few matches,
     the short new grid ends well above where they're looking — leaving them staring at the
     "Recently viewed" section below, with no sign anything happened. If the grid is now
     essentially scrolled off the top of the viewport, bring the results back into view.
     Does nothing when the grid is still on screen (the common case of filtering from the
     top), so it never yanks the page while you're working the sidebar. runSearch's own
     scroll container for the sidebar is separate; this is the whole-window scroll. */
  function scrollResultsIntoViewIfHidden() {
    if (!gridEl) return;
    // Called from renderGrid's finally, i.e. AFTER the grid DOM is updated and the browser
    // has reflowed/clamped scroll to the shorter document. If the grid now sits essentially
    // off the top of the viewport (bottom above the top 25%), the shopper is looking past
    // it — jump instantly to the results. Instant, not smooth: a smooth scroll fired here
    // gets cancelled by the same-tick reflow and ends stuck at the clamped page bottom
    // (verified). No requestAnimationFrame — the instant scroll lands correctly straight
    // away, and rAF would also silently never run in a backgrounded tab.
    const rect = gridEl.getBoundingClientRect();
    if (rect.bottom < window.innerHeight * 0.25) {
      root.scrollIntoView({ block: 'start' });
    }
  }

  // Run a filter-originated search. The scroll correction is applied from renderGrid's
  // finally (guaranteed to run after the grid repaints), flagged here.
  function runFilterSearch() {
    pendingScrollCorrection = true;
    pushHistory();
    runSearch();
  }

  function renderPagination(total) {
    const totalPages = Math.ceil(total / PER_PAGE);
    const currentPage = Math.floor(currentOffset / PER_PAGE) + 1;
    paginationEl.innerHTML = '';
    if (totalPages <= 1) return;

    const nav = document.createElement('nav');
    nav.setAttribute('aria-label', 'Pagination');

    const list = document.createElement('ul');
    list.className = 'pagination relative flex flex-wrap justify-center items-center justify-between w-full mx-auto';

    const prevLi = document.createElement('li');
    prevLi.className = 'pagination__item pagination__item--arrow';
    const prevLink = document.createElement('a');
    prevLink.className = 'pagination__arrow pagination__arrow--prev hover:lighten flex text-current items-center has-ltr-icon';
    prevLink.href = '#';
    prevLink.setAttribute('role', 'link');
    if (currentPage <= 1) prevLink.setAttribute('aria-disabled', 'true');
    prevLink.innerHTML = '<span class="pagination__prev-label mis-2">Previous</span>';
    if (currentPage > 1) {
      prevLink.addEventListener('click', (e) => {
        e.preventDefault();
        goToPage(currentPage - 1);
      });
    }
    prevLi.appendChild(prevLink);
    list.appendChild(prevLi);

    for (const part of buildPageParts(currentPage, totalPages)) {
      const li = document.createElement('li');
      // Show page numbers on ALL breakpoints (owner 2026-07-30) — mobile previously showed only
      // Previous/Next (was `hidden md:block`). buildPageParts keeps the set compact (first, last,
      // current ±1, with … gaps), so it fits phones.
      li.className = 'block';
      if (part.gap) {
        li.innerHTML = '<span class="block leading-none ml-2 mr-2">&hellip;</span>';
      } else if (part.page === currentPage) {
        li.innerHTML = `<span class="pagination__page-current font-bold block leading-none">${part.page}</span>`;
      } else {
        const link = document.createElement('a');
        link.className = 'pagination__page-link text-current block leading-none';
        link.href = '#';
        link.textContent = String(part.page);
        link.addEventListener('click', (e) => {
          e.preventDefault();
          goToPage(part.page);
        });
        li.appendChild(link);
      }
      list.appendChild(li);
    }

    const nextLi = document.createElement('li');
    nextLi.className = 'pagination__item pagination__item--arrow';
    const nextLink = document.createElement('a');
    nextLink.className = 'pagination__arrow pagination__arrow--next hover:lighten flex justify-end text-current items-center has-ltr-icon';
    nextLink.href = '#';
    nextLink.setAttribute('role', 'link');
    if (currentPage >= totalPages) nextLink.setAttribute('aria-disabled', 'true');
    nextLink.innerHTML = '<span class="pagination__next-label text-end">Next</span>';
    if (currentPage < totalPages) {
      nextLink.addEventListener('click', (e) => {
        e.preventDefault();
        goToPage(currentPage + 1);
      });
    }
    nextLi.appendChild(nextLink);
    list.appendChild(nextLi);

    nav.appendChild(list);
    paginationEl.appendChild(nav);
  }

  // The sticky filter sidebar is its own scroll container (#llm-meilisearch-filters, with
  // overflow-y:auto), and filtersEl (its inner content) is wiped and rebuilt on every
  // search. Without saving/restoring, that reset the sidebar to the top — so scrolling
  // down to a brand starting with "H", ticking it, and having the list jump back to the
  // top made picking several values maddening. Restore the exact scroll offset after the
  // rebuild; disjunctive faceting keeps the interacted group's options intact, so its
  // height is stable and the restore lands on the same spot.
  const filtersScrollBox = document.getElementById('llm-meilisearch-filters');

  function renderFacets(facetDistribution) {
    const savedFiltersScroll = filtersScrollBox ? filtersScrollBox.scrollTop : 0;
    const restoreFiltersScroll = () => {
      if (filtersScrollBox) filtersScrollBox.scrollTop = savedFiltersScroll;
    };

    const openFields = new Set(
      [...filtersEl.querySelectorAll('details[open]')].map((d) => d.dataset.field)
    );

    // Preserve "Show more" state across the rebuild. Without this, picking a value from an
    // expanded group (e.g. a brand starting with "H", only visible after Show more) rebuilt
    // the group COLLAPSED back to the first few options — so the list appeared to jump to
    // the top even though the sidebar's own scroll was restored. Capture which groups were
    // expanded AND the scroll offset inside their fixed-height option box, and reapply both.
    const expandedFields = new Set();
    const optionListScroll = {};
    filtersEl.querySelectorAll('details[data-field]').forEach((d) => {
      const f = d.dataset.field;
      const toggle = d.querySelector('.llm-meili-facet__show-more');
      if (toggle && toggle.textContent === 'Show less') expandedFields.add(f);
      const scrollBox = d.querySelector('.llm-meili-facet__options--scroll');
      if (scrollBox) optionListScroll[f] = scrollBox.scrollTop;
    });
    // Option boxes to restore scroll on AFTER the form is in the DOM (scrollTop doesn't
    // stick on a detached element).
    const pendingOptionScrollRestores = [];

    filtersEl.innerHTML = '';

    // Baymard #9: collect the checked groups as we render the sidebar, then show
    // them as removable chips above the product grid (renderActiveChips below).
    const activeGroups = [];

    // Merchant-controlled via Facet Field Config's Field Order (Content >
    // Metaobjects in Shopify admin) - lower numbers first, unranked
    // fields fall after the ranked ones in their natural order.
    const fieldOrder = (field) => FIELD_CONFIG[field]?.fieldOrder ?? null;
    const fields = Object.keys(facetDistribution)
      .filter((f) => !isExcludedField(f))
      .sort((a, b) => {
        const orderA = fieldOrder(a);
        const orderB = fieldOrder(b);
        if (orderA != null && orderB != null) return orderA - orderB;
        if (orderA != null) return -1;
        if (orderB != null) return 1;
        return 0;
      });
    if (fields.length === 0) {
      renderActiveChips([]);
      restoreFiltersScroll();
      return;
    }

    const form = document.createElement('form');
    form.className = 'llm-meili-facets';
    form.addEventListener('submit', (e) => e.preventDefault());

    for (const field of fields) {
      const values = facetDistribution[field];
      const valueEntries = Object.entries(values).filter(([, count]) => count > 0);
      if (valueEntries.length === 0) continue;
      const groupedValues = groupFacetValues(field, valueEntries);

      const details = document.createElement('details');
      details.className = 'llm-meili-facet disclosure';
      details.dataset.field = field;
      if (openFields.has(field) || config.expandFilters) details.open = true;

      const summary = document.createElement('summary');
      summary.textContent = fieldLabel(field);
      details.appendChild(summary);

      const list = document.createElement('div');
      list.className = 'llm-meili-facet__options';

      // Accurate-count fetches for groups hidden behind "Show more" are
      // deferred (not skipped forever) - collected here and fired when
      // that button is actually clicked, see below.
      const deferredCountFetches = [];

      groupedValues.forEach((group, index) => {
        const activeSet = activeFilters.get(field);
        const isChecked = activeSet ? group.rawValues.some((v) => activeSet.has(v)) : false;
        if (isChecked) {
          activeGroups.push({ field: field, label: group.label, rawValues: group.rawValues });
        }
        const row = document.createElement('label');
        row.className = 'llm-meili-facet__option';
        if (index >= MAX_VISIBLE_OPTIONS) row.hidden = true;

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = isChecked;
        checkbox.addEventListener('change', () => {
          toggleFilterGroup(field, group.rawValues, checkbox.checked);
        });

        const labelWrap = document.createElement('span');
        labelWrap.className = 'llm-meili-facet__option-label';
        const text = document.createElement('span');
        text.textContent = group.label;
        labelWrap.append(checkbox, text);
        row.appendChild(labelWrap);

        if (config.showFilterCounts) {
          const countEl2 = document.createElement('span');
          countEl2.className = 'llm-meili-facet__option-count';
          countEl2.textContent = `(${group.count})`;
          row.appendChild(countEl2);

          // Meilisearch's facetDistribution counts are per raw value, so
          // a merged group's naive sum over-counts any product whose own
          // variants happen to use more than one of that group's raw
          // strings (common here - e.g. a product with both "MD (24" to
          // 44")" and "Medium (24" to 44" waist)" as separate variant
          // option values gets counted twice). Single-raw-value groups
          // are already exact and skip this; multi-value groups get a
          // follow-up query for the real de-duplicated count.
          //
          // Real bug found investigating "Something went wrong loading
          // results" on mobile: this fired for EVERY multi-value group
          // in EVERY field, including ones still hidden behind "Show
          // more" (row.hidden above) that the user can't even see yet -
          // on a field like Size with a couple dozen groups, one
          // checkbox click could burst a couple dozen parallel requests
          // at Meilisearch directly from the browser. That's a
          // plausible way to trip a rate limit and take the MAIN search
          // request down with it (same shared budget, same moment).
          // Skipping hidden rows cuts the burst to just what's visibly
          // rendered - a group revealed later via "Show more" still
          // gets its accurate count fetched then, not skipped forever.
          if (group.rawValues.length > 1) {
            if (row.hidden) {
              deferredCountFetches.push(() => {
                fetchAccurateGroupCount(field, group.rawValues).then((accurate) => {
                  if (accurate != null) countEl2.textContent = `(${accurate})`;
                });
              });
            } else {
              fetchAccurateGroupCount(field, group.rawValues).then((accurate) => {
                if (accurate != null) countEl2.textContent = `(${accurate})`;
              });
            }
          }
        }

        list.appendChild(row);
      });

      details.appendChild(list);

      if (groupedValues.length > MAX_VISIBLE_OPTIONS) {
        // Beyond this many values, "Show more" reveals them inside a fixed-
        // height scroll box instead of expanding the whole sidebar downward
        // (owner: a 20+ value list "expands way too much").
        const SCROLL_AFTER = 12;
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'llm-meili-facet__show-more link';
        toggle.textContent = 'Show more';
        let expanded = false;
        const setExpanded = (on) => {
          expanded = on;
          if (on) {
            list.querySelectorAll('.llm-meili-facet__option[hidden]').forEach((el) => {
              el.hidden = false;
            });
            if (deferredCountFetches.length) {
              deferredCountFetches.forEach((fire) => fire());
              deferredCountFetches.length = 0;
            }
            if (groupedValues.length > SCROLL_AFTER) {
              list.classList.add('llm-meili-facet__options--scroll');
            }
            toggle.textContent = 'Show less';
          } else {
            list.classList.remove('llm-meili-facet__options--scroll');
            list.querySelectorAll('.llm-meili-facet__option').forEach((el, i) => {
              el.hidden = i >= MAX_VISIBLE_OPTIONS;
            });
            toggle.textContent = 'Show more';
          }
        };
        toggle.addEventListener('click', () => setExpanded(!expanded));
        details.appendChild(toggle);

        // Reapply the pre-rebuild expansion so picking a value from an expanded list keeps
        // that list open. Scroll offset is restored after the form is attached (below).
        if (expandedFields.has(field)) {
          setExpanded(true);
          if (optionListScroll[field] != null) {
            pendingOptionScrollRestores.push({ list, top: optionListScroll[field] });
          }
        }
      }

      form.appendChild(details);
    }

    if (activeFilters.size > 0) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'llm-meili-clear-all link';
      clearBtn.textContent = 'Clear all filters';
      clearBtn.addEventListener('click', () => {
        activeFilters.clear();
        currentOffset = 0;
        runFilterSearch();
      });
      form.appendChild(clearBtn);
    }

    filtersEl.appendChild(form);
    // Now that the option boxes have layout, restore the scroll offset inside any that
    // were expanded before the rebuild.
    pendingOptionScrollRestores.forEach(({ list, top }) => {
      list.scrollTop = top;
    });
    renderActiveChips(activeGroups);
    restoreFiltersScroll();
  }

  // Baymard #9: applied-filters overview — removable chips above the product grid,
  // so the shopper always sees (and can undo) what's currently narrowing results.
  // Wrapped so a chip glitch can never take down the core facet rendering.
  function ensureChipsBar() {
    let bar = document.getElementById('llm-meili-active-chips');
    if (!bar && gridEl && gridEl.parentNode) {
      bar = document.createElement('div');
      bar.id = 'llm-meili-active-chips';
      bar.className = 'llm-meili-chips';
      bar.hidden = true;
      gridEl.parentNode.insertBefore(bar, gridEl);
    }
    return bar;
  }

  function renderActiveChips(activeGroups) {
    try {
      const bar = ensureChipsBar();
      if (!bar) return;
      bar.innerHTML = '';
      if (!activeGroups || activeGroups.length === 0) {
        bar.hidden = true;
        return;
      }
      bar.hidden = false;

      const heading = document.createElement('span');
      heading.className = 'llm-meili-chips__label';
      heading.textContent = 'Filters:';
      bar.appendChild(heading);

      activeGroups.forEach((g) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'llm-meili-chip';
        chip.setAttribute('aria-label', 'Remove filter: ' + g.label);
        const text = document.createElement('span');
        text.textContent = g.label;
        const x = document.createElement('span');
        x.className = 'llm-meili-chip__x';
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '×';
        chip.append(text, x);
        chip.addEventListener('click', () => toggleFilterGroup(g.field, g.rawValues, false));
        bar.appendChild(chip);
      });

      const clearChip = document.createElement('button');
      clearChip.type = 'button';
      clearChip.className = 'llm-meili-chip llm-meili-chip--clear';
      clearChip.textContent = 'Clear all';
      clearChip.addEventListener('click', () => {
        activeFilters.clear();
        currentOffset = 0;
        runFilterSearch();
      });
      bar.appendChild(clearChip);
    } catch (err) {
      console.error('[llm-meilisearch] chips render failed:', err);
    }
  }

  // rawValues is every raw index value merged into the checked/unchecked
  // group (usually just one, more when the merchant has mapped several
  // raw values to the same display label) - all added/removed together.
  function toggleFilterGroup(field, rawValues, isChecked) {
    if (!activeFilters.has(field)) activeFilters.set(field, new Set());
    const set = activeFilters.get(field);
    for (const value of rawValues) {
      if (isChecked) set.add(value);
      else set.delete(value);
    }
    if (set.size === 0) activeFilters.delete(field);

    currentOffset = 0;
    runFilterSearch();
  }

  // One Section-Rendering-API fetch per handle -> its card <li>. Used for cards
  // the server didn't already paint.
  function fetchCardLi(handle) {
    return fetch(`/collections/${config.collectionHandle}/products/${handle}?section_id=api-product-card`)
      .then((res) => (res.ok ? res.text() : ''))
      .catch(() => '')
      .then((html) => {
        /* null = skip this hit entirely. A deleted or unpublished product can still sit in the
           Meilisearch index, and the Section Rendering API answers 200 for it with `product` unset
           — product-card.liquid then paints its onboarding placeholder ("Product title", the
           default lamp, $12.34). res.ok is true, so the empty-string guard above never catches it
           and a fake product lands in the grid. Same bug the search page had; caught there first
           (owner 2026-09-05). A real card always carries data-product-id. */
        if (!html || html.indexOf('data-product-id') === -1) return null;
        const li = document.createElement('li');
        li.className = 'js-pagination-result';
        li.innerHTML = html;
        return li;
      });
  }

  let firstRender = true;

  // Wrapper so the filter-change scroll correction ALWAYS runs after the grid DOM has been
  // updated — the inner impl has several exit points and async card fetches that can reject,
  // and finally covers all of them (resolve, early return, or throw).
  async function renderGrid(hits) {
    try {
      return await renderGridImpl(hits);
    } finally {
      // First correctly-sorted paint is done: reveal the real cards and drop the
      // loading skeletons (CSS hides them once the grid carries [data-ms-ready]).
      gridEl.setAttribute('data-ms-ready', '1');
      if (pendingScrollCorrection) {
        pendingScrollCorrection = false;
        scrollResultsIntoViewIfHidden();
      }
    }
  }

  async function renderGridImpl(hits) {
    const wasFirst = firstRender;
    firstRender = false;

    const serverLis = wasFirst
      ? gridEl.querySelectorAll(':scope > li[data-handle]')
      : [];

    // Resilience for the nightly re-import window (or any brief Meilisearch
    // hiccup): on the initial, unfiltered page-1 load, if the search returns
    // EMPTY or fewer products than the server already painted, keep the complete
    // server-rendered grid rather than shrinking it or flashing "no results" -
    // the server HTML is always a valid, complete page 1.
    if (
      serverLis.length > 0 &&
      activeFilters.size === 0 &&
      currentOffset === 0 &&
      hits.length < serverLis.length
    ) {
      gridEl.removeAttribute('aria-busy');
      return;
    }

    if (hits.length === 0) {
      gridEl.innerHTML = '<li class="col-span-full">No products match the selected filters.</li>';
      gridEl.removeAttribute('aria-busy');
      return;
    }

    // Instant-load reconciliation against the server-rendered page 1. On the
    // FIRST search after load, REUSE every card the server already painted -
    // just move it into Meilisearch's order - and fetch ONLY the handles the
    // server didn't render. In the common case that's zero re-fetches: products
    // stay visible from the very first paint with no flash, and the final order
    // is still exactly Meilisearch's Featured order (so the sort is never
    // altered even when the synced collection order has drifted a little).
    if (serverLis.length > 0) {
      const reusable = new Map();
      serverLis.forEach((li) => reusable.set(li.getAttribute('data-handle'), li));

      const lisInOrder = await Promise.all(
        hits.map((hit) => reusable.get(hit.handle) || fetchCardLi(hit.handle))
      );

      // filter(Boolean): fetchCardLi returns null for a stale index entry (see its note).
      const frag = document.createDocumentFragment();
      lisInOrder.filter(Boolean).forEach((li) => frag.appendChild(li));
      gridEl.replaceChildren(frag);
      gridEl.removeAttribute('aria-busy');
      return;
    }

    // No server-rendered DOM (subsequent searches, filtered results): fetch
    // every card's real HTML in parallel, kept in Meilisearch's own order
    // regardless of which fetch resolves first.
    const lisInOrder = await Promise.all(hits.map((hit) => fetchCardLi(hit.handle)));
    // filter(Boolean): fetchCardLi returns null for a stale index entry (see its note).
    const frag = document.createDocumentFragment();
    lisInOrder.filter(Boolean).forEach((li) => frag.appendChild(li));
    gridEl.replaceChildren(frag);
    gridEl.removeAttribute('aria-busy');
  }

  sortEl.addEventListener('change', () => {
    if (restoringState) return; // programmatic sync during a history restore — not a user action
    currentSort = sortEl.value;
    currentOffset = 0;
    pushHistory();
    runSearch();
  });

  // Safety net for the deferred initial facet build: if the shopper opens the Filter drawer (or
  // toggles the desktop sidebar) BEFORE the idle callback has run it, force it in synchronously
  // on pointerdown — before the drawer paints — so the drawer is never briefly empty. Harmless
  // once it's already run (pendingFacetBuild is null). Capture phase so it beats the open handler.
  document.querySelectorAll('.js-toggle-filters').forEach((btn) => {
    btn.addEventListener('pointerdown', () => { if (pendingFacetBuild) pendingFacetBuild(); }, { capture: true });
  });

  // We manage scroll position on Back/Forward ourselves (see the popstate handler below). Tell
  // the browser not to ALSO restore the previous scroll offset for these in-collection history
  // entries — otherwise it wins the race and drops you back to your old spot.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  // Back/Forward: rebuild state from the URL and re-render (facets, pagination and grid all
  // read the restored activeFilters / currentSort / currentOffset). No pushHistory here.
  window.addEventListener('popstate', () => {
    applyStateFromUrl();
    syncSortUi();
    runSearch();
    // Reposition to the top of the results, matching forward pagination (goToPage). Without
    // this, Back from page 2 leaves you at the OLD scroll spot on page 1 — the bottom, down in
    // "Recently viewed", where you were when you clicked to page 2. Instant (not smooth) so it
    // lands immediately and can't be cancelled by the grid re-render on the same tick.
    root.scrollIntoView({ block: 'start' });
  });

  // Initial load: adopt any state already in the URL (a shared link, a refresh on page 2,
  // or Back into the collection), normalise the URL, then do the first search.
  applyStateFromUrl();
  syncSortUi();
  history.replaceState({ ms: true }, '', currentStateUrl());
  runSearch();
})();
